import { create } from 'zustand';
import { STORAGE_KEYS, getItem } from '@/services/storage';
import * as authApi from '@/services/auth-api';
import type { AuthUser, TenantKey } from '@/services/auth-api';

type Status = 'unknown' | 'authed' | 'guest';

interface AuthState {
  status: Status;
  user: AuthUser | null;
  bootstrap: () => Promise<void>;
  signIn: (username: string, password: string, tenant: TenantKey) => Promise<void>;
  signOut: () => Promise<void>;
  markUnauthenticated: () => void;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'unknown',
  user: null,

  /** Read persisted token + user on app boot. */
  bootstrap: async () => {
    const [token, userJson] = await Promise.all([
      getItem(STORAGE_KEYS.ACCESS_TOKEN),
      getItem(STORAGE_KEYS.USER),
    ]);
    if (token && userJson) {
      try {
        set({ status: 'authed', user: JSON.parse(userJson) as AuthUser });
        return;
      } catch {
        /* corrupt store; fall through to guest */
      }
    }
    set({ status: 'guest', user: null });
  },

  signIn: async (username, password, tenant) => {
    const user = await authApi.login(username, password, tenant);
    set({ status: 'authed', user });
  },

  signOut: async () => {
    await authApi.logout();
    set({ status: 'guest', user: null });
  },

  markUnauthenticated: () => set({ status: 'guest', user: null }),

  hasPermission: (permission) => {
    return get().user?.permissions.includes(permission) ?? false;
  },
}));
